import { useState } from 'react'
import { Organisation } from '../../services/api/types'
import DomainVerificationModal from '../settings/DomainVerificationModal'
import { api } from '../../services'

interface DomainVerificationBannerProps {
  organisation: Organisation
  onUpdate?: () => void
  onVerificationComplete?: () => void
}

const DomainVerificationBanner: React.FC<DomainVerificationBannerProps> = ({
  organisation,
  onUpdate,
  onVerificationComplete
}) => {
  const [showModal, setShowModal] = useState(false)
  const [fullOrg, setFullOrg] = useState<Organisation | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  // Don't show if verified
  if (organisation.domain_verified) {
    return null
  }

  const handleVerified = () => {
    setShowModal(false)
    // Refresh the page to update all components with verified status
    window.location.reload()
  }

  const handleVerifyClick = async () => {
    // Fetch full organisation details to get verification token
    setIsLoading(true)
    try {
      const orgId = organisation.organisation_id || organisation.id
      if (orgId) {
        const full = await api.organisations.get(orgId)
        setFullOrg(full)
        setShowModal(true)
      }
    } catch (error) {
      console.error('Failed to load organisation details:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 dark:border-yellow-600 p-4 mb-6">
        <div className="flex items-start">
          {/* Use same icon as trial banner for consistency */}
          <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <div className="ml-3 flex-1">
            <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
              Domain Verification Required
            </p>
            <p className="mt-1 text-sm text-yellow-700 dark:text-yellow-300">
              You must verify ownership of <strong>{organisation.target_domain}</strong>. No campaigns will be sent to domains without verified ownership.
            </p>
            <button
              onClick={handleVerifyClick}
              disabled={isLoading}
              className="mt-2 text-sm font-medium text-yellow-800 dark:text-yellow-200 hover:text-yellow-900 dark:hover:text-yellow-100 underline disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Loading...' : 'Verify domain now →'}
            </button>
          </div>
          {/* Banner is not dismissible - verification is required to use the service */}
        </div>
      </div>

      {showModal && fullOrg && (
        <DomainVerificationModal
          organisation={fullOrg}
          onClose={() => setShowModal(false)}
          onVerified={handleVerified}
        />
      )}
    </>
  )
}

export default DomainVerificationBanner
